/**
 * Deciding whether a request just changed something a dashboard is counting.
 *
 * Lives apart from index.tsx so it can be tested: index.tsx mounts the app as a
 * side effect of being imported, so the rules below would otherwise only be
 * exercisable by running the whole application.
 *
 * The judgement is deliberately coarse. A false positive costs one coalesced
 * refetch of data the user is already looking at. A false negative is a stale
 * dashboard — the bug this exists to fix — so the matching errs wide, and a new
 * endpoint under these prefixes is covered without anyone remembering to add it.
 */

/** Endpoints whose mutations can move a location-dashboard number. */
const DASHBOARD_AFFECTING_PATH = /\/(work-orders|work-order-services|approvals|invoices|invoice-batches)(\/|$|\?)/;

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Reading never changes a count, and the SSE stream is a long-lived GET. */
export function isMutatingMethod(method: string | undefined): boolean {
  return MUTATING_METHODS.has((method ?? 'GET').toUpperCase());
}

export function isDashboardAffectingPath(pathname: string): boolean {
  return DASHBOARD_AFFECTING_PATH.test(pathname);
}

/**
 * Best-effort location for the change.
 *
 * Most work-order routes are keyed by work order, not location, so this is
 * absent more often than not. Subscribers must treat absent as "might be mine"
 * rather than filtering it out.
 */
export function locationIdFromUrl(url: URL): string | undefined {
  const fromPath = url.pathname.match(/\/locations\/([0-9a-fA-F-]{36})/);
  if (fromPath) return fromPath[1];
  return url.searchParams.get('location_id') ?? undefined;
}

/** The URL a fetch() input refers to, or null when it cannot be determined. */
export function urlOf(input: RequestInfo | URL, origin: string): URL | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!raw) return null;
    return new URL(raw, origin);
  } catch {
    return null;
  }
}

/** The method a fetch() call will actually use. */
export function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method;
  if (typeof input !== 'string' && !(input instanceof URL) && 'method' in input) return input.method;
  return 'GET';
}

/**
 * Whether this completed request should announce a dashboard-affecting change.
 * Failed requests changed nothing, so they must not trigger a refetch.
 */
export function shouldAnnounceChange(input: RequestInfo | URL, init: RequestInit | undefined, ok: boolean, origin: string): URL | null {
  if (!ok) return null;
  if (!isMutatingMethod(methodOf(input, init))) return null;
  const url = urlOf(input, origin);
  if (!url || !isDashboardAffectingPath(url.pathname)) return null;
  return url;
}
